import { registerBrick, Config, BrickContext } from 'olympe';
import { ReactBrick, useProperty } from '@olympeio/core';
import React, { useState } from 'react';
import { GridRenderCellParams } from '@mui/x-data-grid';
import { DataGridPro, LicenseInfo, GridToolbar } from '@mui/x-data-grid-pro';
import { ThemeProvider } from '@mui/material/styles';
import { UserAvatar } from '../shared/UserAvatar.jsx';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import { ContentCopy } from '@mui/icons-material';
import { IconButton } from '@mui/material';
import { datagridTheme } from '../shared/_theme.jsx';
import { formatApiDate } from '../../utils/_helpers.js';

export default class MassChangeDatagridBrick extends ReactBrick {
    /**
     * @override
     * @protected
     * @param {!BrickContext} $
     * @returns {function(object): React.ReactElement}
     */
    static getReactComponent($) {
        return (props) => {
            // listen to properties
            const loading = useProperty($, 'Loading');
            let data = useProperty($, 'Data') || [];
            const operation = useProperty($, 'Operation') || [];
            const sortBy = useProperty($, 'Sort By');

            // setup data grid, columns to display
            const licenseKey = Config.getParameter('uiappValues.datagrid_license');
            LicenseInfo.setLicenseKey(licenseKey);

            /**
             * @param {GridRenderCellParams} params
             * @returns {React.ReactNode}
             */
            const approverCellRenderer = (params) => {
                const username = params.value || '';
                const [showCopy, setShowCopy] = useState(false);
                return (
                    <div
                        className={'user-list'}
                        onMouseEnter={() => {
                            setShowCopy(true);
                        }}
                        onMouseLeave={() => {
                            setShowCopy(false);
                        }}
                    >
                        <UserAvatar username={username} />
                        <Tooltip title={username} placement={'top'} arrow>
                            <Typography noWrap={true} className={'user-name'}>
                                <b>{username}</b>
                            </Typography>
                        </Tooltip>
                        {showCopy && (
                            <IconButton
                                style={{
                                    borderRadius: '5px',
                                    marginLeft: '10px',
                                }}
                                onClick={(event) => {
                                    if (username.length > 0) {
                                        navigator.clipboard.writeText(username);
                                    }
                                    event.stopPropagation(); // prevent marking the line as selected
                                    setShowCopy(false);
                                }}
                            >
                                <ContentCopy sx={{ fontSize: '1rem !important' }} />
                            </IconButton>
                        )}
                    </div>
                );
            };

            /**
             *
             * @param {GridRenderCellParams} params
             * @returns {React.ReactNode}
             */
            const delegationCellRenderer = (params) => {
                const isDelegation = params.row.SubActFlag === 'X';
                return isDelegation ? (
                    <div style={{ fontSize: '80%' }}>
                        Delegator: {params.row.Substitutor}
                        <br />
                        From: {params.row.SubStartDate ? formatApiDate(params.row.SubStartDate) : 'N/A'}
                        <br />
                        To: {params.row.SubEndDate ? formatApiDate(params.row.SubEndDate) : 'N/A'}
                    </div>
                ) : undefined;
            };

            /**
             *
             * @param {GridRenderCellParams} params
             * @returns {React.ReactNode}
             */
            const commentCellRenderer = (params) => {
                return (
                    <Tooltip title={params.row.Comment} arrow>
                        <div>{params.row.Comment}</div>
                    </Tooltip>
                );
            };

            /**
             *
             * @param {string} id1
             * @param {string} id2
             * @returns {number}
             */
            const costObjectSortComparator = (id1, id2) => {
                return Number.parseInt(id1) - Number.parseInt(id2);
            };

            const columns = [];
            const defineColumn = (field, headerName, otherProps) => {
                const col = {
                    field,
                    headerName,
                    width: 120,
                    resizable: false,
                    disableColumnMenu: true,
                    sortable: false,
                    filterable: false,
                    hideable: false,
                    pinnable: false,
                    ...otherProps,
                };
                columns.push(col);
                return col;
            };

            defineColumn('RelGroup', 'Release Group', { filterable: true, disableColumnMenu: false });
            defineColumn('RelCode', 'Release Code', { filterable: true, disableColumnMenu: false });
            if (operation !== 'PO') {
                defineColumn('OrderCategory', 'Order Category', { width: 140 });
            }
            defineColumn('AgentType', 'Object Type');
            if (operation === 'PO') {
                defineColumn('PurchGrp', 'Purchase Group', {
                    cellClassName: 'MuiDataGrid-cellBold',
                    sortComparator: costObjectSortComparator,
                });
                defineColumn('PurchGrpDesc', 'Purchase Group name', { width: 200 });
            } else {
                defineColumn('CostElementId', 'Cost Object', {
                    cellClassName: 'MuiDataGrid-cellBold',
                    sortComparator: costObjectSortComparator,
                });
                defineColumn('CostElementDescr', 'Cost Object name', { width: 200 });
                defineColumn('CostObjectTypeDescr', 'Cost Object Type', { width: 200 });
            }
            defineColumn('ApproverName', 'Approver', { width: 300, renderCell: approverCellRenderer });
            defineColumn('Substitutor', 'Delegation', { width: 250, renderCell: delegationCellRenderer });
            defineColumn('Comment', 'Comments', { width: 350, renderCell: commentCellRenderer });

            // check if a column should be used for sorting
            let sortModel;
            const asc = true;
            if (sortBy && columns.some((c) => c.field === sortBy)) {
                sortModel = [{ field: sortBy, sort: asc ? 'asc' : 'desc' }];
            } else {
                sortModel = undefined;
            }

            /**
             * Selection: conditionally fire event On Selected or On Selection Cleared
             * @param {!object | object[]} newSelection An array with the selected row indexes
             */
            const onSelectionModelChange = (newSelection) => {
                const selectedValues = newSelection.map((index) => data[index]);
                $.set('Selected Rows', selectedValues);
            };

            // process data, add unique index prop
            if (!loading) {
                data.forEach((o, i) => {
                    o.id = i;
                });
            } else {
                data = [];
            }

            // render
            return (
                <ThemeProvider theme={datagridTheme}>
                    <DataGridPro
                        columns={columns}
                        rows={data}
                        loading={loading}
                        checkboxSelection
                        sortModel={sortModel}
                        editMode="row"
                        onSelectionModelChange={onSelectionModelChange}
                        components={{ Toolbar: GridToolbar }}
                        componentsProps={{
                            toolbar: { style: { height: '40px' } }, // give enough room for the filter pad number to display entirely
                        }}
                        sx={{ padding: '20px', boxSizing: 'border-box' }}
                        scrollbarSize={17}
                    />
                </ThemeProvider>
            );
        };
    }
}

registerBrick('0181ecf3ed648fcf6474', MassChangeDatagridBrick);
